import React from 'react';
import { Box, Slider, styled } from '@mui/material';

const IntensityBar = styled(Box)<{ intensity: number; isActive?: boolean }>(({ intensity, isActive, theme }) => ({
  width: '8px',
  backgroundColor: isActive ? theme.palette.primary.main : 'rgba(255, 255, 255, 0.3)',
  height: intensity,
  margin: '0 auto',
  transition: 'height 0.3s ease, background-color 0.3s ease',
  borderRadius: '4px',
}));

const StyledSlider = styled(Slider)(({ theme }) => ({
  '& .MuiSlider-rail': {
    height: '8px',
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
    borderRadius: '4px',
    border: 'none',
  },
  '& .MuiSlider-track': {
    height: '8px',
    backgroundColor: theme.palette.primary.main,
    borderRadius: '4px',
    border: 'none',
  },
  '& .MuiSlider-thumb': {
    width: 24,
    height: 24,
    backgroundColor: theme.palette.primary.main,
    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
    '&:hover, &.Mui-focusVisible': {
      boxShadow: '0 0 0 8px rgba(65, 105, 225, 0.16)',
    },
    '&:before': {
      display: 'none',
    },
  },
}));

interface Props {
  value: number;
  onChange: (value: number) => void;
}

export const CustomSlider: React.FC<Props> = ({ value, onChange }) => {
  const calculateIntensity = (position: number): number => {
    const middle = 4;
    const maxHeight = 160;
    const distance = Math.abs(position - middle);
    const intensity = (distance / 3) * maxHeight;
    return Math.max(16, intensity);
  };

  return (
    <Box sx={{ width: '100%', mt: 8, mb: 6 }}>
      <Box 
        sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          mb: 4,
          mx: '9px',
          px: '6px',
          height: 80,
          alignItems: 'flex-end',
          position: 'relative',
        }}
      >
        {[1, 2, 3, 4, 5, 6, 7].map((mark) => (
          <Box 
            key={mark} 
            sx={{
              width: '8px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <IntensityBar 
              intensity={calculateIntensity(mark)}
              isActive={mark === value}
            />
          </Box>
        ))}
      </Box>

      <Box>
        <StyledSlider
          value={value}
          onChange={(_, newValue) => onChange(newValue as number)}
          min={1}
          max={7}
          step={1}
          marks={false}
          sx={{
            padding: '20px 0',
            '& .MuiSlider-rail': {
              width: 'calc(100% - 20px)',
              marginLeft: '10px',
            }
          }}
        />
      </Box>
    </Box>
  );
}; 